import { createSlice } from '@reduxjs/toolkit';

const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    // Theme and appearance
    darkMode: localStorage.getItem('darkMode') === 'true',
    
    // Navigation
    sidebarOpen: false,
    mobileMenuOpen: false,
    
    // Modals and dialogs
    modals: {
      login: false,
      register: false,
      productQuickView: false,
      addressForm: false,
      confirmDialog: false,
      imageGallery: false,
    },
    
    // Loading states
    globalLoading: false,
    pageLoading: false,
    
    // Notifications
    notifications: [],
    
    // Search
    searchOpen: false,
    searchQuery: '',
    searchSuggestions: [],
    
    // Filters and sorting
    filtersOpen: false,
    
    // Product view preferences
    productViewMode: localStorage.getItem('productViewMode') || 'grid', // 'grid' or 'list'
    productsPerPage: parseInt(localStorage.getItem('productsPerPage')) || 20,
    
    // Current page context
    currentPage: 'home',
    breadcrumbs: [],
    
    // Error handling
    globalError: null,
    
    // Confirmation dialog
    confirmDialog: {
      open: false,
      title: '',
      message: '',
      onConfirm: null,
      onCancel: null,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      severity: 'warning', // 'info', 'warning', 'error', 'success'
    },
    
    // Image gallery
    imageGallery: {
      open: false,
      images: [],
      currentIndex: 0,
    },
    
    // Quick view product
    quickViewProduct: null,
    
    // Network status
    isOnline: navigator.onLine,
    
    // Screen size
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
  },
  reducers: {
    // Theme
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
      localStorage.setItem('darkMode', state.darkMode.toString());
    },
    
    setDarkMode: (state, action) => {
      state.darkMode = action.payload;
      localStorage.setItem('darkMode', action.payload.toString());
    },
    
    // Navigation
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },
    
    setMobileMenuOpen: (state, action) => {
      state.mobileMenuOpen = action.payload;
    },
    
    // Modals
    openModal: (state, action) => {
      const modalName = action.payload;
      if (state.modals.hasOwnProperty(modalName)) {
        state.modals[modalName] = true;
      }
    },
    
    closeModal: (state, action) => {
      const modalName = action.payload;
      if (state.modals.hasOwnProperty(modalName)) {
        state.modals[modalName] = false;
      }
    },
    
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(key => {
        state.modals[key] = false;
      });
    },
    
    // Loading states
    setGlobalLoading: (state, action) => {
      state.globalLoading = action.payload;
    },
    
    setPageLoading: (state, action) => {
      state.pageLoading = action.payload;
    },
    
    // Notifications
    addNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.notifications.push(notification);
    },
    
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      state.notifications = state.notifications.filter(
        notification => notification.id !== notificationId
      );
    },
    
    clearNotifications: (state) => {
      state.notifications = [];
    },
    
    // Search
    setSearchOpen: (state, action) => {
      state.searchOpen = action.payload;
    },
    
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    
    setSearchSuggestions: (state, action) => {
      state.searchSuggestions = action.payload;
    },
    
    clearSearch: (state) => {
      state.searchQuery = '';
      state.searchSuggestions = [];
    },
    
    // Filters
    setFiltersOpen: (state, action) => {
      state.filtersOpen = action.payload;
    },
    
    toggleFilters: (state) => {
      state.filtersOpen = !state.filtersOpen;
    },
    
    // Product view preferences
    setProductViewMode: (state, action) => {
      state.productViewMode = action.payload;
      localStorage.setItem('productViewMode', action.payload);
    },
    
    setProductsPerPage: (state, action) => {
      state.productsPerPage = action.payload;
      localStorage.setItem('productsPerPage', action.payload.toString());
    },
    
    // Page context
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    },
    
    addBreadcrumb: (state, action) => {
      state.breadcrumbs.push(action.payload);
    },
    
    // Error handling
    setGlobalError: (state, action) => {
      state.globalError = action.payload;
    },
    
    clearGlobalError: (state) => {
      state.globalError = null;
    },
    
    // Confirmation dialog
    openConfirmDialog: (state, action) => {
      state.confirmDialog = {
        open: true,
        ...action.payload,
      };
    },
    
    closeConfirmDialog: (state) => {
      state.confirmDialog = {
        open: false,
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        severity: 'warning',
      };
    },
    
    // Image gallery
    openImageGallery: (state, action) => {
      const { images, currentIndex = 0 } = action.payload;
      state.imageGallery = {
        open: true,
        images,
        currentIndex,
      };
    },
    
    closeImageGallery: (state) => {
      state.imageGallery = {
        open: false,
        images: [],
        currentIndex: 0,
      };
    },
    
    setImageGalleryIndex: (state, action) => {
      state.imageGallery.currentIndex = action.payload;
    },
    
    nextImage: (state) => {
      const { images, currentIndex } = state.imageGallery;
      if (currentIndex < images.length - 1) {
        state.imageGallery.currentIndex = currentIndex + 1;
      }
    },
    
    previousImage: (state) => {
      const { currentIndex } = state.imageGallery;
      if (currentIndex > 0) {
        state.imageGallery.currentIndex = currentIndex - 1;
      }
    },
    
    // Quick view
    setQuickViewProduct: (state, action) => {
      state.quickViewProduct = action.payload;
      if (action.payload) {
        state.modals.productQuickView = true;
      } else {
        state.modals.productQuickView = false;
      }
    },
    
    // Network status
    setOnlineStatus: (state, action) => {
      state.isOnline = action.payload;
    },
    
    // Screen size
    setScreenSize: (state, action) => {
      const { width } = action.payload;
      state.isMobile = width < 768;
      state.isTablet = width >= 768 && width < 1024;
      state.isDesktop = width >= 1024;
    },
  },
});

export const {
  // Theme
  toggleDarkMode,
  setDarkMode,
  
  // Navigation
  toggleSidebar,
  setSidebarOpen,
  toggleMobileMenu,
  setMobileMenuOpen,
  
  // Modals
  openModal,
  closeModal,
  closeAllModals,
  
  // Loading
  setGlobalLoading,
  setPageLoading,
  
  // Notifications
  addNotification,
  removeNotification,
  clearNotifications,
  
  // Search
  setSearchOpen,
  setSearchQuery,
  setSearchSuggestions,
  clearSearch,
  
  // Filters
  setFiltersOpen,
  toggleFilters,
  
  // Product view
  setProductViewMode,
  setProductsPerPage,
  
  // Page context
  setCurrentPage,
  setBreadcrumbs,
  addBreadcrumb,
  
  // Error handling
  setGlobalError,
  clearGlobalError,
  
  // Confirmation dialog
  openConfirmDialog,
  closeConfirmDialog,
  
  // Image gallery
  openImageGallery,
  closeImageGallery,
  setImageGalleryIndex,
  nextImage,
  previousImage,
  
  // Quick view
  setQuickViewProduct,
  
  // Network status
  setOnlineStatus,
  
  // Screen size
  setScreenSize,
} = uiSlice.actions;

export default uiSlice.reducer;

// Selectors
export const selectDarkMode = (state) => state.ui.darkMode;
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectMobileMenuOpen = (state) => state.ui.mobileMenuOpen;
export const selectModals = (state) => state.ui.modals;
export const selectGlobalLoading = (state) => state.ui.globalLoading;
export const selectPageLoading = (state) => state.ui.pageLoading;
export const selectNotifications = (state) => state.ui.notifications;
export const selectSearchOpen = (state) => state.ui.searchOpen;
export const selectSearchQuery = (state) => state.ui.searchQuery;
export const selectProductViewMode = (state) => state.ui.productViewMode;
export const selectCurrentPage = (state) => state.ui.currentPage;
export const selectBreadcrumbs = (state) => state.ui.breadcrumbs;
export const selectConfirmDialog = (state) => state.ui.confirmDialog;
export const selectImageGallery = (state) => state.ui.imageGallery;
export const selectQuickViewProduct = (state) => state.ui.quickViewProduct;
export const selectIsOnline = (state) => state.ui.isOnline;
export const selectIsMobile = (state) => state.ui.isMobile;
export const selectIsTablet = (state) => state.ui.isTablet;
export const selectIsDesktop = (state) => state.ui.isDesktop;